import { CHATBOT_BLOCK_FIELD_DISCLOSURE_VALUE } from "./constants";
import dynamic from "next/dynamic";
import { cn } from "utils/cn";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import cuid from "cuid";

const DataSourceDebug = dynamic(() =>
  import("./DataSourceDebug").then((mod) => mod.DataSourceDebug)
);

type BotMessageProps = {
  chatbotName?: string;
  children: React.ReactNode;
  displayDisclosure?: boolean;
  disclosureText?: string;
  errorCode?: string;
  isError?: boolean;
  isHidden?: boolean;
  isLoading?: boolean;
  renderDebug?: (source: string) => React.ReactNode;
  renderFeedback?: () => React.ReactNode;
  source?: string;
};

export function BotMessage({
  chatbotName,
  children,
  displayDisclosure,
  disclosureText = CHATBOT_BLOCK_FIELD_DISCLOSURE_VALUE,
  errorCode,
  isError = false,
  isHidden = false,
  isLoading = false,
  renderDebug = (src) => <DataSourceDebug>{src}</DataSourceDebug>,
  renderFeedback,
  source,
}: BotMessageProps) {
  return (
    <div
      className={cn(
        "mr-5 animate-fadeIn flex-col items-stretch gap-3 rounded-xl border border-zi-primary/20 px-5 py-4 font-sans text-base",
        {
          "bg-zi-lightDanger text-zi-danger": isError,
          "bg-zi-lightPrimary text-zi-text": !isError,
          hidden: isHidden,
          flex: !isHidden,
        }
      )}
    >
      <div className="relative flex items-center justify-between font-sans">
        <p className="text-sm font-semibold leading-[17px]">{chatbotName}</p>
        {isLoading && (
          <svg
            className="h-[18px] w-[18px] animate-spin"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 15.75C12.7279 15.75 15.75 12.7279 15.75 9C15.75 5.27208 12.7279 2.25 9 2.25C5.27208 2.25 2.25 5.27208 2.25 9C2.25 10.8115 2.96359 12.4563 4.125 13.6687"
              stroke="currentColor"
              strokeOpacity="0.6"
              strokeWidth="4"
            />
          </svg>
        )}
        {!isLoading && typeof renderFeedback === "function" && (
          <div className="absolute bottom-full right-0 h-6 translate-y-3/4 empty:hidden">
            {renderFeedback()}
          </div>
        )}
      </div>
      {typeof children === "string"
        ? children
            .split("\\n")
            .map((line) => ({ id: cuid(), line }))
            .map(({ id, line }) => (
              <p className="whitespace-pre empty:hidden" key={id}>
                {line.replace(/\\"/g, '"')}
              </p>
            ))
        : children}
      <p className="font-mono text-xs font-semibold leading-[15px] before:content-['['] after:content-[']'] empty:hidden">
        {errorCode}
      </p>
      {!isError && !isHidden && source && renderDebug(source)}
      {!isError && !isHidden && displayDisclosure && (
        <ReactMarkdown
          allowedElements={["p", "b", "i", "em", "strong", "a"]}
          remarkPlugins={[remarkGfm]}
          className=" text-[13px] font-normal leading-6 text-zi-secondaryText transition-shadow [&_a:focus:hover]:ring-zi-primary [&_a:focus]:outline-none [&_a:focus]:ring-1 [&_a:focus]:ring-zi-primary [&_a:focus]:ring-offset-2 [&_a:focus]:ring-offset-zi-lightPrimary [&_a:hover]:bg-zi-primary/20 [&_a:hover]:text-zi-primary [&_a:hover]:ring-1 [&_a:hover]:ring-transparent [&_a:hover]:ring-offset-2 [&_a:hover]:ring-offset-zi-primary/20 [&_a]:rounded-sm [&_a]:bg-zi-lightPrimary [&_a]:underline [&_a]:underline-offset-1"
        >
          {disclosureText}
        </ReactMarkdown>
      )}
    </div>
  );
}
